import { useRef } from 'react';

/**
 * Сетка IPv4: 32 бита группами по 8, «бит над битом».
 *
 * rows: [{ label, bits?, dotted? }]
 * editableLabel — если задан, у этой строки ввод (bits из value)
 * highlight — индексы битов (0..31) для подсветки
 * value — строка до 32 символов (0/1/пробел) для редактируемой строки
 */
export default function TrainerIpBits({
  rows = [],
  editableLabel = null,
  value = '',
  onChange,
  highlight = [],
  locked = false,
}) {
  const inputRefs = useRef([]);
  const highlightSet = new Set(highlight || []);
  const bitsValue = String(value || '').padEnd(32, ' ').slice(0, 32);

  function octetDisplay(raw, o) {
    const slice = raw.slice(o * 8, o * 8 + 8);
    return Array.from({ length: 8 }, (_, b) => {
      const ch = slice[b];
      return ch === '0' || ch === '1' ? ch : '';
    }).join('');
  }

  function patchBits(prev, mutate) {
    const chars = String(prev || '').padEnd(32, ' ').slice(0, 32).split('');
    while (chars.length < 32) chars.push(' ');
    mutate(chars);
    return chars.join('');
  }

  function setOctet(octetIndex, digits) {
    if (locked || !onChange) return;
    const clean = String(digits || '').replace(/[^01]/g, '').slice(0, 8);
    const start = octetIndex * 8;
    onChange((prev) =>
      patchBits(prev, (chars) => {
        for (let b = 0; b < 8; b += 1) {
          chars[start + b] = clean[b] || ' ';
        }
      }),
    );
  }

  /** Вставка: разложить биты начиная с октета octetIndex */
  function applyPaste(octetIndex, text) {
    if (locked || !onChange) return;
    const clean = String(text || '').replace(/[^01]/g, '');
    if (!clean) return;
    const start = octetIndex * 8;
    const max = Math.min(clean.length, 32 - start);
    onChange((prev) =>
      patchBits(prev, (chars) => {
        for (let i = 0; i < max; i += 1) {
          chars[start + i] = clean[i];
        }
      }),
    );
    const endBit = start + max - 1;
    const focusOctet = Math.min(3, Math.floor(endBit / 8));
    const caretInOctet = Math.min(8, (endBit % 8) + 1);
    requestAnimationFrame(() => {
      const el = inputRefs.current[focusOctet];
      if (!el) return;
      el.focus();
      el.setSelectionRange(caretInOctet, caretInOctet);
    });
  }

  function handleKeyDown(e, octetIndex) {
    if (locked) return;
    const el = e.target;
    const val = el.value;
    const start = el.selectionStart ?? val.length;
    const end = el.selectionEnd ?? val.length;

    if (e.key === 'Backspace' && start === 0 && end === 0 && octetIndex > 0) {
      e.preventDefault();
      const prev = inputRefs.current[octetIndex - 1];
      if (prev) {
        prev.focus();
        const len = prev.value.length;
        prev.setSelectionRange(len, len);
      }
      return;
    }

    if (e.key === 'ArrowLeft' && start === 0 && end === 0 && octetIndex > 0) {
      e.preventDefault();
      const prev = inputRefs.current[octetIndex - 1];
      if (prev) {
        prev.focus();
        const len = prev.value.length;
        prev.setSelectionRange(len, len);
      }
      return;
    }

    if (
      e.key === 'ArrowRight' &&
      start === val.length &&
      end === val.length &&
      octetIndex < 3
    ) {
      e.preventDefault();
      const next = inputRefs.current[octetIndex + 1];
      if (next) {
        next.focus();
        next.setSelectionRange(0, 0);
      }
    }
  }

  function handleChange(e, octetIndex) {
    if (locked || !onChange) return;
    const clean = e.target.value.replace(/[^01]/g, '').slice(0, 8);
    setOctet(octetIndex, clean);
    if (clean.length === 8 && octetIndex < 3) {
      requestAnimationFrame(() => {
        const next = inputRefs.current[octetIndex + 1];
        if (next) {
          next.focus();
          next.select();
        }
      });
    }
  }

  function handlePaste(e, octetIndex) {
    if (locked) return;
    const text = e.clipboardData?.getData('text') || '';
    if (!/[01]/.test(text)) return;
    e.preventDefault();
    applyPaste(octetIndex, text);
  }

  function renderBits(bits, { editable } = {}) {
    const raw = String(bits || '').padEnd(32, ' ').slice(0, 32);
    const octets = [];
    for (let o = 0; o < 4; o += 1) {
      if (editable) {
        octets.push(
          <input
            key={o}
            ref={(el) => {
              inputRefs.current[o] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={8}
            spellCheck={false}
            autoComplete="off"
            className="trainer-ip-octet-input"
            value={octetDisplay(raw, o)}
            disabled={locked}
            onChange={(e) => handleChange(e, o)}
            onKeyDown={(e) => handleKeyDown(e, o)}
            onPaste={(e) => handlePaste(e, o)}
            aria-label={`Октет ${o + 1}`}
          />,
        );
      } else {
        const cells = [];
        for (let b = 0; b < 8; b += 1) {
          const i = o * 8 + b;
          const ch = raw[i];
          const isHi = highlightSet.has(i);
          cells.push(
            <span
              key={i}
              className={[
                'trainer-ip-bit',
                isHi ? 'is-highlight' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {ch === '0' || ch === '1' ? ch : '·'}
            </span>,
          );
        }
        octets.push(
          <span key={o} className="trainer-ip-octet">
            {cells}
          </span>,
        );
      }
    }
    return <div className="trainer-ip-bits-row">{octets}</div>;
  }

  return (
    <div className="trainer-ip-bits">
      {rows.map((row) => {
        const editable = Boolean(editableLabel) && row.label === editableLabel;
        return (
          <div key={row.label} className="trainer-ip-line">
            <span className="trainer-ip-label">{row.label}</span>
            <div className="trainer-ip-line-body">
              {renderBits(editable ? bitsValue : row.bits, { editable })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Десятичный блок: подписи + dotted.decimal */
export function TrainerIpDecimal({ rows = [] }) {
  return (
    <div className="trainer-ip-decimal">
      {rows.map((row) => (
        <div key={row.label} className="trainer-ip-line">
          <span className="trainer-ip-label">{row.label}</span>
          <code className="trainer-ip-dotted-only">{row.dotted || ''}</code>
        </div>
      ))}
    </div>
  );
}

/** Нормализация ответа сетки → 32 символа 0/1 или null */
export function normalizeIpBitsAnswer(raw) {
  const s = String(raw || '').replace(/[^01]/g, '');
  return s.length === 32 ? s : null;
}
